/**
 * The closed state of a scope dropdown.
 * For Media Devoted | Created By Ciro Autiero
 */

import { forwardRef } from 'react';
import { ChevronDown, X } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { ScopeOption } from './types';

/** forwardRef so Radix's Popover.Trigger can anchor to the real button. */
export const ScopeComboboxTrigger = forwardRef<
  HTMLButtonElement,
  {
    label: string;
    placeholder: string;
    selected?: ScopeOption;
    onClear: () => void;
  } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ label, placeholder, selected, onClear, className, ...rest }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-label={`${label} scope`}
    className={cn(
      'flex h-7 w-auto max-w-[20rem] items-center gap-1.5 rounded-md border border-input bg-background px-2 text-xs',
      'focus:outline-none focus:ring-2 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
      // The floor applies only while EMPTY. The placeholder ("All countries
      // (default)") is the longest thing the button ever shows, so a fixed
      // width sized for it strands a stretch of dead space inside the border
      // the moment a short name like "Australia" is picked. Selected, the
      // button is exactly as wide as what it says.
      selected ? 'border-primary/50 font-medium text-foreground' : 'min-w-[10rem]',
      className,
    )}
    {...rest}
  >
    {selected?.icon ? (
      <span className="shrink-0" aria-hidden>
        {selected.icon}
      </span>
    ) : null}
    {/* min-w-0: `truncate` sets overflow-hidden, which pins a flex item at its
     *  content width — without this a long campaign name shoves the ✕ out. */}
    <span className={cn('min-w-0 truncate', !selected && 'text-muted-foreground')}>
      {selected ? selected.label : placeholder}
    </span>
    {selected ? (
      <X
        className="h-3 w-3 shrink-0 text-muted-foreground hover:text-foreground"
        aria-hidden
        onClick={(e) => {
          e.stopPropagation();
          onClear();
        }}
      />
    ) : (
      <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
    )}
  </button>
));

ScopeComboboxTrigger.displayName = 'ScopeComboboxTrigger';
